import { useState } from 'react'
import { GOAL_CARD_TEXT } from '@anqi/shared'
import type { Action, GoalCardId, PlayerView, Pos } from '../protocol-types'
import { seatStyle } from './Board'

/**
 * 行动区。
 *
 * 这里承载整个界面最硬的一条要求：**任何消耗回合的操作都必须二次确认**。
 * 手机上误触代价太大 —— 一局只有 45 个回合，点错一次就是白扔一手，
 * 而三个人不能通话，误触之后连解释都没法解释。
 *
 * 具体是：
 *   落子 / 放禁区 —— 两步（先在棋盘选格，再按确认）
 *   指控         —— 三步（选对手 → 选卡 → 确认），因为它既费回合又会反噬
 *   亮牌探宝     —— 两步（它会把自己的牌永久公开，换取额外禁区标记）
 *
 * 组件对外只暴露 `onAct`，没有任何一条路径能让一次点击直接发出动作。
 */

export type Mode = 'place' | 'block'

interface ActionBarProps {
  view: PlayerView
  mode: Mode
  onModeChange(m: Mode): void
  /** 棋盘上已选中的格子；未选时为 null */
  selected: Pos | null
  onClearSelection(): void
  onAct(action: Action): void
  /** 上一次动作被服务端拒绝的原因。必须显示，不能静默 */
  lastError: string | null
}

const posKey = (p: Pos) => `${p.row},${p.col}`

export function ActionBar({
  view,
  mode,
  onModeChange,
  selected,
  onClearSelection,
  onAct,
  lastError,
}: ActionBarProps) {
  const [accusing, setAccusing] = useState(false)
  const myTurn = view.current === view.me.seat && !view.ended

  // 可点性一律查服务端给的合法行动，不在这里重算规则。
  const canPlace = view.myLegalActions.some((a) => a.type === 'place')
  const canBlock = view.myLegalActions.some((a) => a.type === 'block')
  const canReveal = view.myLegalActions.some((a) => a.type === 'reveal')
  const accusable = view.myLegalActions.filter(
    (a): a is Extract<Action, { type: 'accuse' }> => a.type === 'accuse',
  )

  // 选中的格子对当前模式是否真的合法 —— 防止「切换模式后旧选中还留着」
  const selectionValid =
    selected !== null &&
    view.myLegalActions.some((a) => a.type === mode && posKey((a as { pos: Pos }).pos) === posKey(selected))

  return (
    <div className="space-y-3">
      {lastError !== null && (
        // 服务端已经把错误分好类：要么是给玩家看的解释，要么是固定通用文案。
        // 两种都原样显示 —— 点了没反应又不知道为什么是最坏的结果。
        <p role="alert" className="rounded-md bg-red-950/60 px-3 py-2 text-sm text-red-200">
          {lastError}
        </p>
      )}

      {!myTurn ? (
        <p className="py-2 text-center text-sm text-slate-400">
          {view.ended ? '对局已结束' : `等 ${seatStyle(view.current).label}方 行动`}
        </p>
      ) : (
        <>
          {/* 模式切换：决定点格子是落子还是放禁区 */}
          <div className="flex gap-2">
            <ModeButton
              active={mode === 'place'}
              disabled={!canPlace}
              onClick={() => {
                onModeChange('place')
                onClearSelection()
              }}
            >
              落子 · 余 {view.me.stonesLeft}
            </ModeButton>
            <ModeButton
              active={mode === 'block'}
              disabled={!canBlock}
              onClick={() => {
                onModeChange('block')
                onClearSelection()
              }}
            >
              禁区 · 余 {view.me.blocksLeft}
            </ModeButton>
          </div>

          {/* 第二步：确认。没选格子时按钮不可用，且提示先选 */}
          <button
            type="button"
            disabled={!selectionValid}
            onClick={() => {
              if (selected === null) return
              onAct(mode === 'place' ? { type: 'place', pos: selected } : { type: 'block', pos: selected })
            }}
            className="min-h-[52px] w-full rounded-lg bg-amber-500 text-base font-bold text-slate-900 disabled:bg-slate-700 disabled:text-slate-500"
          >
            {selectionValid
              ? `确认${mode === 'place' ? '落子' : '放禁区'}（第 ${selected!.row + 1} 行 ${selected!.col + 1} 列）`
              : `先在棋盘上选一格`}
          </button>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={accusable.length === 0}
              onClick={() => setAccusing(true)}
              className="min-h-[48px] flex-1 rounded-lg border border-slate-600 text-sm font-semibold disabled:opacity-40"
            >
              指控
            </button>
            <RevealButton disabled={!canReveal} bonus={view.config.revealBonusBlocks} onAct={onAct} />
          </div>
        </>
      )}

      {accusing && (
        <AccuseDialog
          view={view}
          accusable={accusable}
          onCancel={() => setAccusing(false)}
          onConfirm={(action) => {
            setAccusing(false)
            onAct(action)
          }}
        />
      )}
    </div>
  )
}

function ModeButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean
  disabled: boolean
  onClick(): void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'min-h-[48px] flex-1 rounded-lg text-sm font-semibold',
        active ? 'bg-slate-100 text-slate-900' : 'border border-slate-600 text-slate-200',
        'disabled:opacity-40',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/** 亮牌也要两步：它会把自己的目标卡永久公开给两个对手。 */
function RevealButton({
  disabled,
  bonus,
  onAct,
}: {
  disabled: boolean
  bonus: number
  onAct(a: Action): void
}) {
  const [confirming, setConfirming] = useState(false)
  if (!confirming) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setConfirming(true)}
        className="min-h-[48px] flex-1 rounded-lg border border-slate-600 text-sm font-semibold disabled:opacity-40"
      >
        亮牌探宝
      </button>
    )
  }
  return (
    <div className="flex-1 rounded-lg border border-amber-500 p-2 text-xs">
      <p className="mb-2 leading-snug text-amber-200">
        亮牌后两个对手都会看到你的目标卡，换 {bonus} 个禁区标记。不可撤销。
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            setConfirming(false)
            onAct({ type: 'reveal' })
          }}
          className="min-h-[44px] flex-1 rounded bg-amber-500 font-bold text-slate-900"
        >
          确认亮牌
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="min-h-[44px] flex-1 rounded border border-slate-600"
        >
          取消
        </button>
      </div>
    </div>
  )
}

/**
 * 指控对话框 —— 三步。
 *
 * 为什么给它最多摩擦：指控猜错不但白扔一个回合，还会被罚停一手（看配置），
 * 而且暴露了自己在想什么。这是全局代价最大的一次点击。
 */
function AccuseDialog({
  view,
  accusable,
  onCancel,
  onConfirm,
}: {
  view: PlayerView
  accusable: Array<Extract<Action, { type: 'accuse' }>>
  onCancel(): void
  onConfirm(a: Extract<Action, { type: 'accuse' }>): void
}) {
  const [target, setTarget] = useState<number | null>(null)
  const [guess, setGuess] = useState<GoalCardId | null>(null)

  // 能指控谁、能猜哪张卡，都来自服务端给的合法行动列表。
  // 已亮牌的对手不在列表里，所以不必在这里另做判断。
  const targets = [...new Set(accusable.map((a) => a.target))]
  const guessesFor = (t: number) =>
    [...new Set(accusable.filter((a) => a.target === t).map((a) => a.guess))]

  return (
    <div className="rounded-lg border border-red-500/60 bg-slate-900 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-bold text-red-300">指控（猜错要停一手）</span>
        <button type="button" onClick={onCancel} className="min-h-[44px] px-3 text-sm text-slate-400">
          取消
        </button>
      </div>

      {/* 第一步：选对手 */}
      <p className="mb-1 text-xs text-slate-400">① 指控谁</p>
      <div className="mb-3 flex gap-2">
        {targets.map((t) => {
          const opp = view.opponents.find((o) => o.seat === t)
          return (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTarget(t)
                setGuess(null)
              }}
              className={[
                'min-h-[48px] flex-1 rounded-lg text-sm font-semibold',
                target === t ? 'bg-slate-100 text-slate-900' : 'border border-slate-600',
              ].join(' ')}
            >
              {opp?.displayName ?? `${seatStyle(t as 0 | 1 | 2).label}方`}
            </button>
          )
        })}
      </div>

      {/* 第二步：选卡 */}
      {target !== null && (
        <>
          <p className="mb-1 text-xs text-slate-400">② 猜他手里是哪张</p>
          <div className="mb-3 grid grid-cols-2 gap-2">
            {guessesFor(target).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGuess(g)}
                className={[
                  'min-h-[48px] rounded-lg px-2 text-left text-xs leading-tight',
                  guess === g ? 'bg-slate-100 text-slate-900' : 'border border-slate-600',
                ].join(' ')}
              >
                <span className="block font-bold">{GOAL_CARD_TEXT[g].name}</span>
                <span className="block opacity-70">{GOAL_CARD_TEXT[g].description}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* 第三步：确认 */}
      <button
        type="button"
        disabled={target === null || guess === null}
        onClick={() => {
          if (target === null || guess === null) return
          onConfirm({ type: 'accuse', target: target as 0 | 1 | 2, guess })
        }}
        className="min-h-[52px] w-full rounded-lg bg-red-500 text-base font-bold text-white disabled:bg-slate-700 disabled:text-slate-500"
      >
        ③ 确认指控
      </button>
    </div>
  )
}
